import type { ReactNode } from 'react';

import { General } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';
import type { Size } from './types';

export type CompanyOption = {
  value: string;
  label: string;
  icon?: ReactNode;
};

export type CompanyFilterSelectProps = {
  companyOptions: CompanyOption[];
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: Size;
  disabled?: boolean;
  /** Show the selected label/placeholder text. Default true. */
  showText?: boolean;
  /** Show the dropdown chevron. Default true. */
  showChevron?: boolean;
  bordered?: boolean;
  shape?: 'rounded' | 'pill' | 'square';
  color?: ColorName;
  tone?: Style;
  tooltip?: ReactNode;
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
};

export default function CompanyFilterSelect({
  companyOptions,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select company',
  className,
  size,
  disabled,
  showText,
  showChevron,
  bordered,
  shape,
  color,
  tone,
  tooltip,
  onClear,
}: CompanyFilterSelectProps) {
  const selectedCompany = companyOptions.find((c) => c.value === value);

  const options = companyOptions.map((company) => ({
    value: company.value,
    label: company.label,
    icon: company.icon ?? <General.Building07 />,
  }));

  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const select = (
    <FilterSelect
      icon={selectedCompany?.icon ?? <General.Building07 />}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoWidth
      size={size}
      showText={showText}
      showChevron={showClearButton ? false : showChevron}
      bordered={bordered}
      shape={shape}
      color={color}
      tone={tone}
      tooltip={tooltip}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
